import type React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type SuccessNoticeProps = {
    /** Already localized. The caller owns its own copy and interpolation. */
    message: string;
    /** Optional way onward. Omit it when the reader is already looking at it. */
    action?: { label: string; to: string };
    /** Names the dismiss control. Icon-only buttons need a name. */
    dismissLabel: string;
    onDismiss: () => void;
    /**
     * Literal static prefix for this instance's test ids — `<testId>`, plus
     * `-message`, `-link` and `-dismiss`.
     *
     * Pages normally hardcode their own ids rather than taking one as a prop.
     * A shared notice is the documented exception, and `PageNotice` already
     * takes exactly this prop for exactly this reason: two of these can sit on
     * one screen, so the id has to say which confirmation it is.
     */
    testId: string;
};

/**
 * "That worked" — a dismissible banner, optionally carrying a link onward.
 *
 * **A banner rather than a toast**, and this app has no toast host mounted
 * anywhere on purpose. `RecordedNotice` made the argument first: a confirmation
 * that carries a link is a link the user has four seconds to click, and a link
 * they have four seconds to click is a link they will miss.
 *
 * It exists as a shared component because the shape had been hand-copied twice
 * before a third caller wanted it — the operations Record tab and the year-end
 * panel both draw this exact bar. Those two are **not** folded in here: each
 * carries page-specific branching around its message and its link, and both are
 * pinned by tests that assert their own ids. This is the component their next
 * change can move onto, not a refactor smuggled into an unrelated one.
 *
 * The `action` is optional because the same confirmation is worth showing in
 * two places with different endings: from the Control Center, saving an
 * employee should offer the register; on the register itself, the reader is
 * already looking at the list the employee just joined, and a link back to the
 * page you are on is noise.
 */
export const SuccessNotice: React.FC<SuccessNoticeProps> = ({
    message,
    action,
    dismissLabel,
    onDismiss,
    testId,
}) => (
    <div
        className="flex flex-wrap items-center gap-3 rounded-2xl bg-success-soft px-4 py-3 text-sm text-success-soft-foreground"
        {...getNodeTestIdProp(testId)}
    >
        <Icon aria-hidden icon="gravity-ui:circle-check" />
        <span {...getNodeTestIdProp(`${testId}-message`)}>{message}</span>
        {action ? (
            <Link
                className="font-medium underline underline-offset-2"
                to={action.to}
                {...getNodeTestIdProp(`${testId}-link`)}
            >
                {action.label}
            </Link>
        ) : null}
        <Button
            aria-label={dismissLabel}
            className="ml-auto"
            isIconOnly
            onPress={onDismiss}
            size="sm"
            variant="ghost"
            {...getNodeTestIdProp(`${testId}-dismiss`)}
        >
            <Icon aria-hidden icon="gravity-ui:xmark" />
        </Button>
    </div>
);
